import { useEffect, useMemo, useRef, useState } from 'react'
import { apiJson, ApiError } from '../api'
import { arcadeCss } from '../arcade/arcadeCss'
import { resolveArcadeTheme, type ArcadeTheme } from '../arcade/themes'
import { usePrefersReducedMotion } from '../arcade/useArcadeTheme'
import {
  avatarSprite,
  PixelAvatar,
  PixelLogo,
  Sparkline,
  SurfaceSplitNote,
  TopicBars,
  TopicShareBar,
} from '../arcade/atoms'
import { BuildLogView } from '../arcade/buildLogView'
import { LeagueView, RagView, SpendersPanels, TaxonomyView, TeamWarsRace, ToolsView } from '../arcade/views'
import { resolveTab, SubPanel, SubTabs, VIEW_TABS } from '../arcade/tabs'
import { LAYOUTS } from '../arcade/skeletons'
import { UsageCard } from '../arcade/skeletons'
import {
  CALENDAR_WINDOWS,
  classFor,
  figure,
  fmtTokens,
  levelOf,
  ROLLING_WINDOWS,
  surfaceOf,
  tagsFor,
  toViewModel,
  type ArcadeWindow,
  type LeaderboardPayload,
  type Mode,
  type Player,
  type TopicCount,
} from '../arcade/viewModel'

/**
 * The CHU-GPT staff arcade at arcade.chugpt.apps.chu.com.au.
 *
 * This page owns three things and delegates everything else:
 *
 *   1. THE ONE FETCH. `GET /api/leaderboard?window=` is called here and
 *      nowhere else. No theme and no layout gets an endpoint or a field of its
 *      own — a skeleton is handed a model, never a URL.
 *   2. THE SHELL. Header, controls, the "you" anchor and the drill-downs are
 *      shared by every layout, which is what makes mobile one design instead
 *      of eight. There is a single markup tree for the shell; the phone
 *      treatment is entirely CSS (see `arcadeCss`), so no phone-only tree can
 *      drift from the desktop one.
 *   3. THE DISPATCH. `theme.layout` selects a skeleton from `LAYOUTS`.
 *
 * Governance: hide-costs is enforced SERVER-SIDE (`backend/src/routes/
 * leaderboard.ts` sends `spend: null` and `move: null`, and the settings
 * client fails closed on a Secrets Manager error). Client-side, the payload is
 * converted once by `toViewModel` into a `BoardModel` that has no `spend` and
 * no `move` field at all, so no skeleton can render a withheld figure or a
 * mover — see `arcade/viewModel.ts` for why that is a type-level property
 * rather than a convention.
 */

// `PixelLogo` moved to `arcade/atoms.tsx` (23 Sep 2026, round 10) so the
// staff portal's Home tab can share the same mascot component under its own
// header text, rather than a second copy of the canvas-blit logic.

/** `GET /api/leaderboard/team?name=` — the Team Wars drill-down. */
interface TeamDetail {
  department: string
  window: string
  hideCosts: boolean
  members: Player[]
  topics: TopicCount[]
}

/** `GET /api/leaderboard/topic?name=&window=` — the Taxonomy topic drill-down. */
interface TopicDetail {
  topic: string
  window: string
  teams: Array<{ department: string; count: number }>
  contributors: Array<{ id: string; name: string; count: number }>
  daily: Array<{ day: string; count: number }>
  models: Array<{ model: string; count: number }>
}

/** `GET /api/leaderboard/user?id=&window=` — the per-player profile drill-down. */
interface UserProfile extends Player {
  window: string
  hideCosts: boolean
  rank: number
  totalPlayers: number
  topics: TopicCount[]
  /** Whether this profile's CHAT / CLAUDE CODE halves mean anything. Absent
   *  (an older server) is the same state of knowledge as false: unknown. */
  surfaceSplitAvailable?: boolean
}

/** `#rrggbb` -> `rgba(r,g,b,alpha)`. The rain trail is a low-alpha wash of the
 *  theme's own ground; a canvas fillStyle cannot read a CSS custom property. */
function hexAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return hex
  const n = parseInt(m[1]!, 16)
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`
}

/** The Team Wars drill-down: one department's roster plus that team's own
 *  taxonomy. These endpoints return the raw wire `Player`, so every figure
 *  here goes through `figure()` — the arcade's single currency formatter. */
function TeamDetailView({
  detail,
  loading,
  error,
  teamColors,
  onBack,
  onSelectPlayer,
}: {
  detail: TeamDetail | null
  loading: boolean
  error: string | null
  teamColors: string[]
  onBack: () => void
  onSelectPlayer: (id: string) => void
}) {
  const members = detail ? [...detail.members].sort((a, b) => b.tokens - a.tokens) : []
  return (
    <div className="teamdetail">
      <button type="button" className="back" onClick={onBack}>
        ← BACK TO TEAM WARS
      </button>
      {loading ? <p className="hint">LOADING ROSTER…</p> : null}
      {error ? (
        <p className="hint" style={{ color: 'var(--a-hot)' }}>
          {error}
        </p>
      ) : detail ? (
        <>
          <h2>{detail.department}</h2>
          <p className="sub">
            {members.length} player{members.length === 1 ? '' : 's'} · window {detail.window.toUpperCase()}
          </p>
          {members.length === 0 ? (
            <p className="hint">NO PLAYERS IN THIS TEAM FOR THIS WINDOW.</p>
          ) : (
            <div className="members">
              {members.map((m, i) => {
                const cls = classFor(m)
                const badges = tagsFor(m, i, members.length)
                return (
                  <div
                    className={`member clickable${m.you ? ' you' : ''}`}
                    key={m.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectPlayer(m.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onSelectPlayer(m.id)
                      }
                    }}
                  >
                    <PixelAvatar name={m.name} size={40} />
                    <div style={{ minWidth: 0 }}>
                      <div className="mname">{m.name}</div>
                      <div className="mcls">{cls.role}</div>
                      <div className="mstats">
                        <span>
                          TOK <b>{fmtTokens(m.tokens)}</b>
                        </span>
                        <span>
                          LVL <b>{levelOf(m.tokens)}</b>
                        </span>
                        {m.streak >= 1 ? (
                          <span>
                            STREAK <b>{m.streak}D</b>
                          </span>
                        ) : null}
                        {detail.hideCosts ? null : (
                          <span>
                            SPEND <b>{figure(m.spend).text}</b>
                          </span>
                        )}
                      </div>
                      {badges.length > 0 ? (
                        <div className="badges">
                          {badges.map((b) => (
                            <span key={b.label} className={`bdg ${b.kind}`}>
                              {b.label}
                            </span>
                          ))}
                        </div>
                      ) : null}
                      <Sparkline data={m.spark} />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          <div className="taxhead">TEAM TAXONOMY — WHAT {detail.department.toUpperCase()} USES CHU-GPT FOR</div>
          <TopicBars topics={detail.topics} colors={teamColors} />
        </>
      ) : null}
    </div>
  )
}

/** A tiny CRT trend line for the topic drill-down's daily count series. */
function TrendLine({ data }: { data: Array<{ day: string; count: number }> }) {
  if (data.length === 0) return <p className="hint">NO DAILY TREND DATA IN THIS WINDOW.</p>
  return <Sparkline data={data.map((d) => d.count)} />
}

/** The Taxonomy topic drill-down. */
function TopicDetailView({
  detail,
  loading,
  error,
  teamColors,
  onBack,
  onSelectPlayer,
}: {
  detail: TopicDetail | null
  loading: boolean
  error: string | null
  teamColors: string[]
  onBack: () => void
  onSelectPlayer: (id: string) => void
}) {
  return (
    <div className="teamdetail">
      <button type="button" className="back" onClick={onBack}>
        ← BACK TO TAXONOMY
      </button>
      {loading ? <p className="hint">LOADING TOPIC…</p> : null}
      {error ? (
        <p className="hint" style={{ color: 'var(--a-hot)' }}>
          {error}
        </p>
      ) : detail ? (
        <>
          <h2>{detail.topic}</h2>
          <p className="sub">window {detail.window.toUpperCase()}</p>

          <div className="taxhead">BY TEAM</div>
          <TopicBars topics={detail.teams.map((t) => ({ topic: t.department, count: t.count }))} colors={teamColors} />

          <div className="taxhead" style={{ marginTop: 24 }}>
            TOP CONTRIBUTORS
          </div>
          {detail.contributors.length === 0 ? (
            <p className="hint">NO CONTRIBUTOR DATA IN THIS WINDOW.</p>
          ) : (
            <div className="contributors">
              {detail.contributors.map((c) => (
                <button type="button" className="chip" key={c.id} onClick={() => onSelectPlayer(c.id)}>
                  <PixelAvatar name={c.name} size={24} />
                  <span className="cname">{c.name}</span>
                  <span className="ccount">{c.count.toLocaleString()}</span>
                </button>
              ))}
            </div>
          )}

          <div className="taxhead" style={{ marginTop: 24 }}>
            DAILY TREND
          </div>
          <TrendLine data={detail.daily} />

          <div className="taxhead" style={{ marginTop: 24 }}>
            TOP MODELS
          </div>
          {detail.models.length === 0 ? (
            <p className="hint">NO MODEL DATA IN THIS WINDOW.</p>
          ) : (
            <div className="contributors">
              {detail.models.map((m) => (
                <span className="chip static" key={m.model}>
                  <span className="cname">{m.model}</span>
                  <span className="ccount">{m.count.toLocaleString()}</span>
                </span>
              ))}
            </div>
          )}
        </>
      ) : null}
    </div>
  )
}

/** Fixed card geometry shared by the offscreen PNG renderer, drawn at 2x. */
const CARD_W = 420
const CARD_H = 560
const CARD_SCALE = 2

/**
 * Renders one player's arcade card to a PNG blob via an offscreen canvas, for
 * the "Save/Copy card" share action. Draws NOTHING but what is already in the
 * data-minimized `UserProfile` shape — no email, ever — and takes its cost
 * figure from `figure()` like everything else.
 */
async function renderProfileCard(profile: UserProfile, theme: ArcadeTheme): Promise<Blob | null> {
  const canvas = document.createElement('canvas')
  canvas.width = CARD_W * CARD_SCALE
  canvas.height = CARD_H * CARD_SCALE
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.scale(CARD_SCALE, CARD_SCALE)

  const fd = theme.fontDisplay
  const fb = theme.fontBody
  try {
    await Promise.all([document.fonts.load(`16px ${fd}`), document.fonts.load(`26px ${fb}`)])
  } catch {
    // Best-effort — draw with whatever font is actually available.
  }

  ctx.fillStyle = theme.panel
  ctx.fillRect(0, 0, CARD_W, CARD_H)
  ctx.strokeStyle = theme.line
  ctx.lineWidth = 2
  ctx.strokeRect(4, 4, CARD_W - 8, CARD_H - 8)

  ctx.fillStyle = theme.gold
  ctx.font = `${11 * theme.fdScale}px ${fd}`
  ctx.fillText('CHU-GPT ARCADE', 28, 40)

  const { hue, on } = avatarSprite(profile.name)
  const cell = 22
  const gridPx = cell * 5
  const originX = (CARD_W - gridPx) / 2
  const originY = 64
  ctx.fillStyle = theme.avatarBg
  ctx.fillRect(originX - 6, originY - 6, gridPx + 12, gridPx + 12)
  ctx.fillStyle = `hsl(${hue} 90% 60%)`
  for (let y = 0; y < 5; y++) {
    for (let x = 0; x < 5; x++) {
      if (on[y]?.[x]) ctx.fillRect(originX + x * cell, originY + y * cell, cell, cell)
    }
  }

  const cls = classFor(profile)
  ctx.textAlign = 'center'
  ctx.fillStyle = theme.accent
  ctx.font = `${26 * theme.fsScale}px ${fb}`
  ctx.fillText(profile.name, CARD_W / 2, originY + gridPx + 40)
  ctx.fillStyle = theme.cyan
  ctx.font = `${11 * theme.fdScale}px ${fd}`
  ctx.fillText(cls.role.toUpperCase(), CARD_W / 2, originY + gridPx + 62)
  ctx.textAlign = 'left'

  const statsTop = originY + gridPx + 92
  ctx.font = `${16 * theme.fdScale}px ${fd}`
  const lvl = levelOf(profile.tokens)
  const stats: Array<[string, string]> = [
    ['RANK', `#${profile.rank} / ${profile.totalPlayers}`],
    ['LEVEL', String(lvl)],
    ['TOKENS', fmtTokens(profile.tokens)],
    ['SPEND', figure(profile.spend).text],
    ['STREAK', `${profile.streak}D`],
  ]
  stats.forEach(([label, value], i) => {
    const y = statsTop + i * 30
    ctx.fillStyle = theme.inkDim
    ctx.font = `${11 * theme.fdScale}px ${fd}`
    ctx.fillText(label, 28, y)
    ctx.fillStyle = theme.ink
    ctx.font = `${16 * theme.fdScale}px ${fd}`
    ctx.fillText(value, 150, y)
  })

  const badges = tagsFor(profile, profile.rank - 1, profile.totalPlayers).slice(0, 3)
  let by = statsTop + stats.length * 30 + 14
  ctx.font = `${9 * theme.fdScale}px ${fd}`
  let bx = 28
  for (const b of badges) {
    const label = b.label
    const padX = 8
    const w = ctx.measureText(label).width + padX * 2
    if (bx + w > CARD_W - 28) {
      bx = 28
      by += 30
    }
    const badgeColor = b.kind === 'gold' ? theme.gold : b.kind === 'hot' ? theme.hot : theme.inkFaint
    ctx.strokeStyle = badgeColor
    ctx.strokeRect(bx, by - 16, w, 22)
    ctx.fillStyle = badgeColor
    ctx.fillText(label, bx + padX, by)
    bx += w + 8
  }

  ctx.fillStyle = theme.inkDim
  ctx.font = `${9 * theme.fdScale}px ${fd}`
  ctx.fillText('CHUGPT.APPS.CHU.COM.AU/ARCADE', 28, CARD_H - 28)

  return await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'))
}

/** Builds the staff-only deep link to one profile: `?u=<opaque id>`. */
function profileShareUrl(id: string): string {
  const onArcadeHost = window.location.hostname.startsWith('arcade.')
  const path = onArcadeHost ? '/' : '/arcade'
  const url = new URL(path, window.location.origin)
  url.searchParams.set('u', id)
  return url.toString()
}

/** The profile drill-down, rendered as an overlay so it can be opened from
 *  anywhere a player appears without losing the caller's scroll position. */
function ProfileModal({
  id,
  profile,
  theme,
  loading,
  error,
  onClose,
}: {
  id: string
  profile: UserProfile | null
  theme: ArcadeTheme
  loading: boolean
  error: string | null
  onClose: () => void
}) {
  const [shareStatus, setShareStatus] = useState<string | null>(null)
  const [cardBusy, setCardBusy] = useState(false)
  // Built through the SAME seam function the board rows use, so a person's
  // profile and their row can never describe their window differently. Unknown
  // for a null profile, which is the loading/error state and knows nothing.
  const surface = profile ? surfaceOf(profile, profile.surfaceSplitAvailable === true) : { known: false as const }

  async function handleCopyLink() {
    const url = profileShareUrl(id)
    try {
      await navigator.clipboard.writeText(url)
      setShareStatus('LINK COPIED')
    } catch {
      setShareStatus(url)
    }
    window.setTimeout(() => setShareStatus(null), 3500)
  }

  async function handleCopyCard() {
    if (!profile) return
    setCardBusy(true)
    try {
      const blob = await renderProfileCard(profile, theme)
      if (!blob) {
        setShareStatus('COULD NOT RENDER CARD')
        return
      }
      const canWriteImage =
        typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard?.write === 'function'
      if (canWriteImage) {
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          setShareStatus('CARD COPIED')
          return
        } catch {
          // Fall through to the download fallback below.
        }
      }
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `chu-gpt-arcade-${profile.name.replace(/\s+/g, '-').toLowerCase()}.png`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      setShareStatus('CARD DOWNLOADED')
    } finally {
      setCardBusy(false)
      window.setTimeout(() => setShareStatus(null), 3500)
    }
  }

  return (
    <div className="modalveil" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={profile ? `${profile.name} profile` : 'Player profile'}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="back" onClick={onClose}>
          ← CLOSE
        </button>
        {loading ? <p className="hint">LOADING PROFILE…</p> : null}
        {error ? (
          <p className="hint" style={{ color: 'var(--a-hot)' }}>
            {error}
          </p>
        ) : profile ? (
          <>
            {/* HEADER BAND. Rank moves out of the six-tile grid and up here
                beside the name: it is what people open this panel for, and as
                one of six equal tiles it was buried. Badges come up with it,
                which costs no height at all. */}
            <div className="phead">
              <PixelAvatar name={profile.name} size={76} photoId={profile.id} window={profile.window} grid={20} />
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: 0 }}>
                  {profile.name}
                  {profile.you ? <span className="youtag">YOU</span> : null}
                </h2>
                <p className="sub" style={{ margin: '4px 0 0' }}>
                  {classFor(profile).role} · {profile.department}
                  {profile.office && profile.office !== 'Unassigned' ? ` · ${profile.office}` : ''} ·
                  window {profile.window.toUpperCase()}
                </p>
                {(() => {
                  const badges = tagsFor(profile, profile.rank - 1, profile.totalPlayers)
                  return badges.length > 0 ? (
                    <div className="badges" style={{ marginTop: 8 }}>
                      {badges.map((bd) => (
                        <span key={bd.label} className={`bdg ${bd.kind}`}>
                          {bd.label}
                        </span>
                      ))}
                    </div>
                  ) : null
                })()}
              </div>
              <div className="rankpill">
                <div className="rv">#{profile.rank}</div>
                <div className="rl">of {profile.totalPlayers}</div>
              </div>
            </div>

            {/* BODY. Wide column for the one thing that genuinely earns the
                width; a 186px rail for the six numbers that do not. */}
            <div className="pbody">
              <div style={{ minWidth: 0 }}>
                <div className="taxhead">
                  <span>Heartbeat</span>
                  <span className="thint">{profile.spark.length} days</span>
                </div>
                {/* Promoted from a 20px sliver to a real chart. It was the most
                    informative thing on the panel and the least legible. */}
                <Sparkline data={profile.spark} height={104} />

                <div className="taxhead" style={{ marginTop: 18 }}>
                  <span>What {profile.name.split(' ')[0]} uses it for</span>
                </div>
                {/* NOT a bar per topic. A dozen independent bars ran off the
                    bottom of the screen and answered a question nobody asked;
                    what a person wants from their own taxonomy is SHARE of
                    their work, which one segmented bar says in a single row. */}
                <TopicShareBar topics={profile.topics} colors={theme.chartColors} />
              </div>

              <div className="prail">
                <div className="taxhead">
                  <span>This window</span>
                </div>
                <div className="statlist">
                  <div className="primary">
                    <span>Tokens</span>
                    <b>{fmtTokens(profile.tokens)}</b>
                  </div>
                  <div className="primary">
                    <span>Streak</span>
                    <b>{profile.streak}D</b>
                  </div>
                  <div>
                    <span>Level</span>
                    <b>{levelOf(profile.tokens)}</b>
                  </div>
                  <div>
                    <span>Models</span>
                    <b>{profile.models}</b>
                  </div>
                  <div>
                    <span>Tenure</span>
                    <b>{profile.tenureDays}D</b>
                  </div>
                  <div>
                    <span>Spend</span>
                    <b data-withheld={figure(profile.spend).withheld ? '1' : '0'}>
                      {figure(profile.spend).text}
                    </b>
                  </div>
                  {/* WHERE THE TOKENS ABOVE CAME FROM. Two rows, not a third
                      total: `Chat + Claude Code` IS `Tokens`, so they are
                      indented under it rather than listed as peers, and there
                      is no `+` anywhere. When the board could not tell the two
                      surfaces apart these rows are ABSENT — a `0` beside
                      "Claude Code" would tell this person's colleagues they
                      write no code, which the board never established. The
                      note below says so once instead. */}
                  {surface.known ? (
                    <>
                      <div className="sfstat">
                        <span>· Chat</span>
                        <b>{surface.chatTokensText}</b>
                      </div>
                      <div className="sfstat">
                        <span>· Claude Code</span>
                        <b>{surface.codeTokensText}</b>
                      </div>
                    </>
                  ) : null}
                </div>
                <SurfaceSplitNote shown={surface.known} />
              </div>
            </div>

            <div className="taxhead" style={{ marginTop: 20 }}>
              SHARE
            </div>
            <div className="sharerow">
              <button type="button" className="win" onClick={handleCopyLink}>
                COPY LINK
              </button>
              <button type="button" className="win" onClick={handleCopyCard} disabled={cardBusy}>
                {cardBusy ? 'RENDERING…' : 'COPY / SAVE CARD'}
              </button>
              {shareStatus ? <span className="sharestatus">{shareStatus}</span> : null}
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}

/** The top-level modes, in order. Declared once at module scope because the
 *  mode picker renders it AND the URL reader validates against it; two copies
 *  is how `?view=` would come to accept something the picker cannot show. */
const MODES: Array<[Mode, string]> = [
  ['spend', 'Big Spenders'],
  ['tokens', 'Token Titans'],
  ['mains', 'Model Mains'],
  ['teams', 'Team Wars'],
  ['rivalry', 'Origin'],
  ['usage', 'Tools'],
  ['rag', 'Knowledge base'],
  ['taxonomy', 'Taxonomy'],
  ['buildlog', "What's new"],
]

/** Plain-text mode names for the tab strip's accessible label. Derived from
 *  MODES rather than written out again. */
const HINT_LABELS: Record<Mode, string> = Object.fromEntries(MODES) as Record<Mode, string>

const HINTS: Record<Mode, string> = {
  spend: 'Ranked by <b>gateway spend</b> this window.',
  tokens: 'Ranked by <b>tokens generated</b> — Haiku mains climb here that don’t top spend.',
  mains: 'Grouped by <b>most-used model</b>, played as a character class. Who mains what.',
  teams: '<b>Department vs department.</b> Total usage per faction.',
  rivalry:
    '<b>Office versus office, team versus team.</b> Scored per active person, so headcount cannot decide it and no one person carries a side. A measure of where the platform is being used, not of how hard anyone is working.',
  usage:
    '<b>Which tools and agents are actually in play.</b> Counted by calls and by how many people reached for them. A tool used twice may have saved more time than one used two hundred times.',
  rag:
    '<b>The knowledge base.</b> What is indexed, how fresh it is, and who actually searches it. Corpus and document only \u2014 no query text appears on this board.',
  taxonomy:
    '<b>What the platform is used for</b> — conversation topics by volume (titles only, no content). Click a topic for its team/people/model breakdown.',
  buildlog:
    '<b>What has shipped, and when.</b> Written by hand when something goes live, in plain words rather than generated from the engineering history — so it is what you can DO, not what was changed.',
}

/**
 * `chrome`, added round 13 for the admin-console Leaderboard section
 * (Maroun: "leaderboard functionality should be all leaderboard
 * functionality we had, into this section, as before" -- every tab, every
 * board, everything BELOW the header, unchanged; only the header itself
 * differs by mount):
 *
 *   standalone  the dedicated arcade host / the staff portal's own tab
 *               (default). Full masthead ("HIGH // SCORES", the coin line),
 *               theme resolved from the admin-set default
 *               (`payload.defaultTheme`, itself defaulting to Bento HQ).
 *   embedded    mounted inside the admin console's own shell, which already
 *               has a header and a brand. The masthead is redundant there
 *               and the cabinet picker offered a choice nothing else in the
 *               admin panel's own look honours, so the theme is pinned to
 *               Bento HQ rather than resolved.
 *
 * BY PROP, NOT CSS. A hidden-by-stylesheet masthead is still in the DOM,
 * still fetching nothing different, and one missed selector away from
 * reappearing; a prop the component itself branches on is the one
 * definition of "embedded" every future change to this page has to update
 * in one place.
 */
export function LeaderboardPage({
  isAdmin,
  onSignOut,
  chrome = 'standalone',
}: {
  isAdmin: boolean
  onSignOut: () => void
  chrome?: 'standalone' | 'embedded'
}) {
  const [payload, setPayload] = useState<LeaderboardPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Mode and sub-tab both come from the URL on first load, so a link to a
  // panel opens on that panel. `view` is validated by the mode list below and
  // `tab` by `resolveTab`, which falls back to the mode's landing tab rather
  // than rendering nothing — a stale link must degrade to the summary, never
  // to a blank view.
  const [mode, setMode] = useState<Mode>(() => {
    const raw = new URLSearchParams(window.location.search).get('view')
    return MODES.some(([m]) => m === raw) ? (raw as Mode) : 'tokens'
  })
  const [tab, setTab] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('view')
    const startMode = MODES.some(([m]) => m === raw) ? (raw as Mode) : 'tokens'
    return resolveTab(startMode, params.get('tab'))
  })
  // Opens on the 1h ROLLING window (CTO request, 8 Sep 2026). Was 'mo'.
  // KNOWN TRADE-OFF: at current volumes an hour is often empty, so the board
  // can land on "nothing happened". That is the point of the ask -- it answers
  // "what is happening right now" rather than "what happened this month" --
  // but if the empty state reads badly in practice, the fix is a better empty
  // state, not silently widening the window behind the label.
  const [win, setWin] = useState<ArcadeWindow>('1h')
  // The mobile header sheet. One markup tree for the controls: on desktop
  // `.topright` is a row in the header, on a phone the same node becomes a
  // sheet this flag opens. No phone-only tree to drift.
  const [menuOpen, setMenuOpen] = useState(false)
  const [teamDetail, setTeamDetail] = useState<TeamDetail | null>(null)
  const [teamLoading, setTeamLoading] = useState(false)
  const [teamError, setTeamError] = useState<string | null>(null)
  const [topicDetail, setTopicDetail] = useState<TopicDetail | null>(null)
  const [topicLoading, setTopicLoading] = useState(false)
  const [topicError, setTopicError] = useState<string | null>(null)
  // The open profile drill-down, keyed on the OPAQUE id (never an email).
  const [profileId, setProfileId] = useState<string | null>(() => {
    const fromQuery = new URLSearchParams(window.location.search).get('u')
    if (fromQuery) return fromQuery
    const fromPath = window.location.pathname.match(/\/arcade\/u\/([a-zA-Z0-9_-]+)/)
    return fromPath ? (fromPath[1] ?? null) : null
  })
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileError, setProfileError] = useState<string | null>(null)
  const rainRef = useRef<HTMLCanvasElement | null>(null)
  /**
   * Reception mode. A URL parameter, NOT a new endpoint and not a payload
   * field: `?tv=1` puts whichever skeleton is active into a presentation state
   * with no controls and nothing to interact with, and CSS scales the type
   * with the viewport so the board reads from across a room. Read once at
   * mount — a screen in reception is not going to change its own URL.
   */
  const [tv] = useState(() => new URLSearchParams(window.location.search).get('tv') === '1')
  // Round 13: no per-viewer override anywhere any more (the cabinet picker
  // is gone from both mounts), so there is nothing left to read from
  // localStorage. `embedded` pins Bento HQ outright; `standalone` still
  // honours the admin-set default (Settings -> Leaderboard default theme),
  // which itself already falls back to Bento HQ when unset
  // (`DEFAULT_ARCADE_THEME_ID`).
  const theme = chrome === 'embedded' ? resolveArcadeTheme('bento') : resolveArcadeTheme(payload?.defaultTheme ?? null)
  const reducedMotion = usePrefersReducedMotion()
  const css = useMemo(() => arcadeCss(theme), [theme])
  const fadeColor = useMemo(() => hexAlpha(theme.bgSolid, 0.09), [theme])

  // THE SEAM. Everything below this line renders from `model`, which has no
  // `spend` and no `move` field. Recomputed on mode/window switch without a
  // refetch: mode is view state, not a query parameter.
  const model = useMemo(
    () => toViewModel(payload ?? { players: [], hideCosts: true, topics: [] }, mode, win),
    [payload, mode, win],
  )

  function openTeam(dept: string) {
    setTeamLoading(true)
    setTeamError(null)
    apiJson<TeamDetail>(`/api/leaderboard/team?name=${encodeURIComponent(dept)}&window=${win}`)
      .then((d) => setTeamDetail(d))
      .catch((err) => setTeamError(err instanceof ApiError ? `${err.message} (HTTP ${err.status})` : String(err)))
      .finally(() => setTeamLoading(false))
  }

  function openTopic(name: string) {
    setTopicLoading(true)
    setTopicError(null)
    apiJson<TopicDetail>(`/api/leaderboard/topic?name=${encodeURIComponent(name)}&window=${win}`)
      .then((d) => setTopicDetail(d))
      .catch((err) => setTopicError(err instanceof ApiError ? `${err.message} (HTTP ${err.status})` : String(err)))
      .finally(() => setTopicLoading(false))
  }

  function openProfile(id: string) {
    setProfileId(id)
  }

  // Leaving Team Wars/Taxonomy (or changing the window) closes any open
  // drill-down — the fetched roster/topic would otherwise silently go stale.
  useEffect(() => {
    setTeamDetail(null)
    setTeamError(null)
    setTopicDetail(null)
    setTopicError(null)
  }, [mode, win])

  useEffect(() => {
    if (!profileId) {
      setProfile(null)
      setProfileError(null)
      return
    }
    let cancelled = false
    setProfileLoading(true)
    setProfileError(null)
    apiJson<UserProfile>(`/api/leaderboard/user?id=${encodeURIComponent(profileId)}&window=${win}`)
      .then((d) => {
        if (!cancelled) setProfile(d)
      })
      .catch((err) => {
        if (!cancelled) setProfileError(err instanceof ApiError ? `${err.message} (HTTP ${err.status})` : String(err))
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [profileId, win])

  // Keeps the address bar in sync with the open profile, the view and its
  // sub-tab, so any link copied out of the bar reopens exactly what is on
  // screen. `replaceState`, not `pushState`: the board is one page, and
  // switching a tab is not navigation to be un-done with Back.
  useEffect(() => {
    const url = new URL(window.location.href)
    if (profileId) url.searchParams.set('u', profileId)
    else url.searchParams.delete('u')
    url.searchParams.set('view', mode)
    if (tab) url.searchParams.set('tab', tab)
    else url.searchParams.delete('tab')
    window.history.replaceState(null, '', url.toString())
  }, [profileId, mode, tab])

  // A mode switch lands on that mode's own landing tab. Carrying a tab id
  // across views is how `?tab=race` would follow the viewer into Taxonomy and
  // render nothing at all.
  function selectMode(next: Mode) {
    setMode(next)
    setTab(resolveTab(next, null))
  }

  const tabs = VIEW_TABS[mode]
  const activeTab = tab ?? ''

  // THE ONE FETCH.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    apiJson<LeaderboardPayload>(`/api/leaderboard?window=${win}`)
      .then((data) => {
        if (!cancelled) setPayload(data)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? `${err.message} (HTTP ${err.status})` : String(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [win])

  // Reception mode rotates the window every 30s so an unattended screen is not
  // frozen on one view. It changes `win`, which the ONE fetch already keys on
  // — no extra endpoint, no polling of its own. Held still under reduced
  // motion, where an unannounced content swap is the thing being avoided.
  useEffect(() => {
    if (!tv || reducedMotion) return
    const order: ArcadeWindow[] = ['day', 'wk', 'mo']
    const id = window.setInterval(() => {
      setWin((w) => order[(order.indexOf(w) + 1) % order.length] ?? 'mo')
    }, 30_000)
    return () => window.clearInterval(id)
  }, [tv, reducedMotion])

  // Spend can't be a mode when costs are hidden — fall back to tokens.
  useEffect(() => {
    if (model.hideCosts && mode === 'spend') setMode('tokens')
  }, [model.hideCosts, mode])

  // Digital rain. Only the Neon Cabinet theme asks for it, and it never runs
  // under prefers-reduced-motion — `reducedMotion` is a live subscription.
  useEffect(() => {
    const c = rainRef.current
    if (!c) return
    const x = c.getContext('2d')
    if (!x) return
    const reduce = reducedMotion
    const fs = 14
    let cols = 0
    let drops: number[] = []
    const glyphs = '01<>[]{}=+*/CHUGPT日ﾊﾐﾋｰｳｼﾅﾓﾆｻﾜ'.split('')
    function size() {
      c!.width = window.innerWidth
      c!.height = window.innerHeight
      cols = Math.floor(c!.width / fs)
      drops = new Array(cols).fill(1)
    }
    size()
    window.addEventListener('resize', size)
    let timer: number | undefined
    if (!reduce) {
      timer = window.setInterval(() => {
        x!.fillStyle = fadeColor
        x!.fillRect(0, 0, c!.width, c!.height)
        x!.font = `${fs}px monospace`
        for (let i = 0; i < cols; i++) {
          x!.fillStyle = Math.random() > 0.975 ? theme.ink : theme.accent
          x!.fillText(glyphs[Math.floor(Math.random() * glyphs.length)] ?? '0', i * fs, drops[i]! * fs)
          if (drops[i]! * fs > c!.height && Math.random() > 0.975) drops[i] = 0
          drops[i]!++
        }
      }, 55)
    }
    return () => {
      window.removeEventListener('resize', size)
      if (timer) window.clearInterval(timer)
    }
  }, [reducedMotion, theme, fadeColor])

  const teamColors = theme.chartColors
  // THE DISPATCH. A theme names a skeleton; the skeleton draws the model.
  const Skeleton = LAYOUTS[theme.layout]

  return (
    <div className="arcade" data-arcade-layout={theme.layout} data-tv={tv ? '1' : '0'}>
      <style>{css}</style>
      {theme.rain ? <canvas id="rain" ref={rainRef} aria-hidden="true" /> : null}
      {theme.scanlines ? <div className="scan" aria-hidden="true" /> : null}
      {theme.vignette ? <div className="vig" aria-hidden="true" /> : null}
      <div className="wrap">
        <header className="top">
          {/* `chrome="embedded"`: the admin console already has its own
              header and brand directly above this mount, so the arcade's
              own masthead (wordmark, "insert prompt" coin line) would be a
              second, contradictory one. Gated by the PROP, not a stylesheet
              -- see the component doc comment. */}
          {chrome !== 'embedded' ? (
            <div className="brand">
              <div className="eyebrow">CHU-GPT ARCADE</div>
              <div className="brandrow">
                <PixelLogo theme={theme} />
                <h1>
                  HIGH <span className="s">//</span> SCORES
                </h1>
              </div>
              <div className="coin">
                <span className="blink">▮</span> INSERT PROMPT TO CONTINUE
              </div>
              <div className="tvclock">
                {win === 'day' ? 'TODAY' : win === 'wk' ? 'THIS WEEK' : win === 'mo' ? 'THIS MONTH' : 'ALL TIME'} ·{' '}
                {model.org.players} PLAYERS · TITLES ONLY
              </div>
            </div>
          ) : null}

          {/* The one control cluster. A row in the desktop header; a sheet on
              a phone, opened by `.menubtn` (which is display:none above the
              mobile breakpoint). */}
          <button
            type="button"
            className="menubtn"
            aria-expanded={menuOpen}
            aria-controls="arcade-menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? '✕' : '☰'} MENU
          </button>
          <div className="headright">
            <div className="topright" id="arcade-menu" data-open={menuOpen ? '1' : '0'}>
              {/* The cabinet picker lived here. Removed round 13 (Maroun):
                  Bento HQ is the only theme reachable anywhere else in the
                  admin panel's own look, so a per-viewer choice among nine
                  cabinets implied a decision the rest of the console does
                  not honour. The admin-set default (Settings) still governs
                  the standalone mount; `embedded` always pins Bento HQ --
                  see the `theme` assignment above. */}
              {isAdmin ? (
                <a
                  className="glink"
                  href={
                    window.location.hostname.startsWith('arcade.')
                      ? `${window.location.protocol}//${window.location.hostname.replace(/^arcade\./, 'admin.')}/overview`
                      : '/overview'
                  }
                >
                  ← ADMIN CONSOLE
                </a>
              ) : null}
              <button type="button" className="glink" onClick={onSignOut}>
                SIGN OUT
              </button>
              <div className="credits">
                PLAYERS <b>{model.org.players}</b>
                <br />
                POT <b data-withheld={model.pot.withheld ? '1' : '0'}>{model.pot.text}</b>
              </div>
            </div>
          </div>
        </header>

        <div className="controls">
          <div className="modes">
            {MODES.filter(([m]) => !(model.hideCosts && m === 'spend')).map(([m, label]) => (
              <button
                key={m}
                type="button"
                className={m === 'tokens' ? 'tab tok' : 'tab'}
                data-on={mode === m ? '1' : '0'}
                onClick={() => selectMode(m)}
              >
                {label}
              </button>
            ))}
          </div>
          {/* Two GROUPS, not one scale. 1H-24H are rolling windows ending
              now; DAY is a Sydney CALENDAR day and is therefore not the same
              thing as 24H. Running them together in one row would invite
              exactly the misreading v0.12.1 had to fix, so they are separated
              and labelled. */}
          <div className="wingroups">
            <div className="wins" role="group" aria-label="Rolling windows">
              <span className="winlab">Rolling</span>
              {ROLLING_WINDOWS.map((w) => (
                <button
                  key={w}
                  type="button"
                  className="win"
                  data-on={win === w ? '1' : '0'}
                  onClick={() => setWin(w)}
                >
                  {w.toUpperCase()}
                </button>
              ))}
            </div>
            <div className="wins" role="group" aria-label="Calendar windows">
              <span className="winlab">Calendar</span>
              {CALENDAR_WINDOWS.map((w) => (
                <button
                  key={w}
                  type="button"
                  className="win"
                  data-on={win === w ? '1' : '0'}
                  onClick={() => setWin(w)}
                >
                  {w.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
        <p className="hint" dangerouslySetInnerHTML={{ __html: HINTS[mode] }} />

        {/* THE SECOND LEVEL. Rendered once, here in the shell, for the same
            reason the window pills are: one strip that every view inherits
            cannot drift from another view's copy of it. Hidden in reception
            mode along with the rest of the controls — a screen on a wall has
            nobody to press a tab. */}
        {tabs && !tv && !loading && !error ? (
          <SubTabs
            tabs={tabs}
            active={activeTab}
            onSelect={setTab}
            label={`${HINT_LABELS[mode]} sections`}
          />
        ) : null}

        {/* The "you" anchor. One node, in normal flow above the board on a
            desktop; CSS turns it into a fixed bottom bar on a phone so it
            stays on screen through the whole scroll. It is the single most
            useful element on a small screen and it used to scroll away at
            row three. */}
        {!loading && !error && model.you && mode !== 'teams' && mode !== 'taxonomy' ? (
          <div className="spotlight">
            <b>◆ YOU ARE RANK {String(model.you.player.rank).padStart(2, '0')}</b>
            <span className="youchase">
              {model.you.chasing ? ` — chasing ${model.you.chasing.first}` : ' — top of the board, defend it'}
            </span>
            <span className="youscore">{model.you.player.score.text}</span>
          </div>
        ) : null}

        {loading ? (
          <p className="hint">LOADING SCORES…</p>
        ) : error ? (
          <p className="hint" style={{ color: 'var(--a-hot)' }}>
            {error}
          </p>
        ) : mode === 'taxonomy' ? (
          topicLoading || topicError || topicDetail ? (
            <TopicDetailView
              detail={topicDetail}
              loading={topicLoading}
              error={topicError}
              teamColors={teamColors}
              onBack={() => {
                setTopicDetail(null)
                setTopicError(null)
              }}
              onSelectPlayer={openProfile}
            />
          ) : (
            <SubPanel id={activeTab}>
              <TaxonomyView model={model} theme={theme} tab={activeTab} onSelectTopic={openTopic} />
            </SubPanel>
          )
        ) : mode === 'usage' ? (
          <SubPanel id={activeTab}>
            <ToolsView model={model} theme={theme} tab={activeTab} />
            {/* The agent board rides with the summary: it is the other half of
                "what is actually in play", and it has always been the correct
                half of this view. */}
            {activeTab === 'summary' ? (
              <div className="usagegrid">
                <UsageCard
                  title="Agents by use"
                  rows={model.usage.agents.map((a) => ({
                    name: a.agent,
                    conversations: a.conversations,
                    people: a.people,
                  }))}
                  empty="No agent usage recorded in this window."
                  note="Reach is the second column: an agent shared with three people cannot out-rank one shared with three hundred."
                  colors={teamColors}
                />
              </div>
            ) : null}
          </SubPanel>
        ) : model.ranked.length === 0 ? (
          <p className="hint">NO SCORES IN THIS WINDOW. INSERT PROMPT TO CONTINUE.</p>
        ) : mode === 'rivalry' ? (
          <SubPanel id={activeTab}>
            <LeagueView model={model} theme={theme} tab={activeTab} />
          </SubPanel>
        ) : mode === 'rag' ? (
          <SubPanel id={activeTab}>
            <RagView model={model} theme={theme} tab={activeTab} />
          </SubPanel>
        ) : mode === 'buildlog' ? (
          <SubPanel id={activeTab}>
            <BuildLogView model={model} tab={activeTab} />
          </SubPanel>
        ) : mode === 'teams' ? (
          teamLoading || teamError || teamDetail ? (
            <TeamDetailView
              detail={teamDetail}
              loading={teamLoading}
              error={teamError}
              teamColors={teamColors}
              onBack={() => {
                setTeamDetail(null)
                setTeamError(null)
              }}
              onSelectPlayer={openProfile}
            />
          ) : activeTab === 'race' ? (
            <SubPanel id={activeTab}>
              <TeamWarsRace model={model} theme={theme} reducedMotion={reducedMotion} />
            </SubPanel>
          ) : (
            <div className="teams">
              {model.teams.map((t, i) => (
                <div
                  className="team clickable"
                  key={t.dept}
                  role="button"
                  tabIndex={0}
                  style={{ ['--pos' as string]: teamColors[i % teamColors.length] }}
                  onClick={() => openTeam(t.dept)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      openTeam(t.dept)
                    }
                  }}
                >
                  <div className="tl">
                    <span className="fac">
                      {i === 0 ? '♛ ' : ''}
                      {t.dept}
                    </span>
                    <span className="amt">{t.text}</span>
                  </div>
                  <div className="track">
                    <div className="fill" style={{ width: `${model.teamMax > 0 ? (t.amt / model.teamMax) * 100 : 0}%` }} />
                  </div>
                  {t.mvp ? (
                    <div className="mvp">
                      MVP <b>{t.mvp.first}</b> · {model.hideCosts ? t.mvp.tokensText : t.mvp.cost.text}
                    </div>
                  ) : null}
                  <div className="drill">VIEW ROSTER →</div>
                </div>
              ))}
            </div>
          )
        ) : mode === 'spend' ? (
          /* BIG SPENDERS LANDS ON THE KPIs AND THE LADDER, and everything that
             used to sit between them and the fold — the race, the trend, the
             enriched board, the group charts — is now a tab. The skeleton is
             the landing content, so it renders on the summary and nowhere
             else: it is the leaderboard, not a header. */
          <SubPanel id={activeTab}>
            <SpendersPanels model={model} theme={theme} tab={activeTab} onSelectPlayer={openProfile} />
            {/* ONE ORG ROW PER PAGE, and this is where the rule is enforced.
                `SpendersPanels` opens with eleven tiles; every skeleton also
                carries its own strip of four or five. Rendered together they
                printed ACTIVE PEOPLE 22 and ACTIVE STAFF 22, TOKENS THIS
                WINDOW twice and COST PER ACTIVE PERSON beside COST PER PERSON
                — the same five numbers under two sets of labels, a screen
                apart, which reads as two different measurements rather than
                one repeated. The enriched row is the superset, so the skeleton
                suppresses its own here and keeps it everywhere else, where it
                is the only org row on the page. */}
            {activeTab === 'summary' ? (
              <Skeleton model={model} theme={theme} onSelectPlayer={openProfile} orgTiles={false} />
            ) : null}
          </SubPanel>
        ) : (
          <Skeleton model={model} theme={theme} onSelectPlayer={openProfile} />
        )}

        <p className="foot">
          Live usage across CHU-GPT. Levels map to cumulative tokens; ranks to standing.
          <br />
          Everyone can see this board.
        </p>
      </div>

      {profileId ? (
        <ProfileModal
          id={profileId}
          profile={profile}
          theme={theme}
          loading={profileLoading}
          error={profileError}
          onClose={() => setProfileId(null)}
        />
      ) : null}
    </div>
  )
}

export default LeaderboardPage
